import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InlineMessage from "../../../common/InlineMessage/InlineMessage";
import { POSITION_OPTIONS } from "../adManagementFormConfig";
import { formikPropType } from "./common/adManagementPropTypes";
import { renderPlaceholderValue } from "./common/renderHelpers";

const BannerPositionTooltipContent = () => {
  const headingSx = {
    color: "#1AAEFA",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.4,
    mb: 1,
  };
  const descSx = {
    color: "#222",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    lineHeight: 1.4,
    textAlign: "left",
  };
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2.5,
        p: 1.25,
        alignItems: "flex-start",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      {/* TOP BANNER column */}
      <Box sx={{ width: 170 }}>
        <Typography sx={headingSx}>TOP BANNER</Typography>
        <Box
          sx={{
            background: "#ffffff",
            borderRadius: "6px",
            p: "10px 0",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            mb: 1.25,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: 34,
              borderRadius: "4px",
              background:
                "linear-gradient(90deg, #2E7BE0 0%, #4FA3FF 50%, #2E7BE0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.4,
            }}
          >
            TOP BANNER (ROTATING)
          </Box>
        </Box>
        <Typography sx={descSx}>
          Rotating carousel at the top of the homepage. Max 10 banners. Rotates
          every 5 seconds.
        </Typography>
      </Box>

      {/* FEATURED BANNER column */}
      <Box sx={{ width: 170 }}>
        <Typography sx={headingSx}>FEATURED BANNER</Typography>
        <Box
          sx={{
            background: "#ffffff",
            borderRadius: "6px",
            p: "10px 0",
            display: "flex",
            gap: "6px",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            mb: 1.25,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: 34,
                borderRadius: "4px",
                background:
                  "linear-gradient(135deg, #7A4BFF 0%, #B85BFF 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.4,
              }}
            >
              FEAT
            </Box>
          ))}
        </Box>
        <Typography sx={descSx}>
          Horizontal scroll section below top banners. No slot limit.
        </Typography>
      </Box>
    </Box>
  );
};

const BannerPositionField = ({
  formik,
  isViewMode,
  isLimited,
  onPositionChange,
}) => (
  <Grid item xs={12} md={6}>
    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
      <Typography variant="caption" fontWeight={600} sx={{ color: "#555" }}>
        Banner Position<span style={{ color: "red", marginLeft: 2 }}>*</span>
      </Typography>
      <Tooltip
        arrow
        placement="right"
        title={<BannerPositionTooltipContent />}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#ffffff !important",
              color: "#222",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              boxShadow: "0 4px 18px rgba(0,0,0,0.12)",
              p: 0,
              maxWidth: "none",
            },
          },
          arrow: {
            sx: {
              color: "#ffffff !important",
              "&::before": {
                backgroundColor: "#ffffff !important",
                border: "1px solid #E0E0E0",
              },
            },
          },
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 16, color: "#1976d2", cursor: "pointer" }}
        />
      </Tooltip>
    </Box>
    <FormControl fullWidth size="small" disabled={isViewMode || isLimited}>
      <Select
        name="position"
        value={formik.values.position}
        onChange={onPositionChange || formik.handleChange}
        onBlur={formik.handleBlur}
        displayEmpty
        renderValue={(val) => renderPlaceholderValue(val, "Select position...")}
      >
        <MenuItem value="" disabled>
          <em>Select position...</em>
        </MenuItem>
        {POSITION_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <InlineMessage
      error={
        formik.touched.position && formik.errors.position
          ? formik.errors.position
          : ""
      }
    />
  </Grid>
);

BannerPositionField.propTypes = {
  formik: formikPropType,
  isViewMode: PropTypes.bool.isRequired,
  isLimited: PropTypes.bool.isRequired,
  onPositionChange: PropTypes.func,
};

BannerPositionField.defaultProps = {
  onPositionChange: null,
};

export default BannerPositionField;
export { BannerPositionTooltipContent };
